// Functions to work with Google Tag Manager(gtg)

import { consoleLogger, environmentService } from '../config/di/container';

export const isGtagLoaded = (): boolean => {
  return !!window.gtag;
};

/**
 * Loads google's gtag.js into the current DOM.
 * Returns a promise which resolves to {@code true} if the script needs to be initialized after.
 */
export function loadGtag(gaTrackingId?: string): Promise<boolean> {
  if (!gaTrackingId) {
    // don't try to load it if no gaTrackingId got provided
    consoleLogger.error('Failed to load pixel', {
      pixel: 'google_analytics',
      reason: 'No id provided!'
    });
    return Promise.resolve(false);
  }

  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`;

  // TODO: logic needs to be updated. only for testing purposes
  if (true) {
    consoleLogger.debug('⚙️ Pixel already attached to DOM', {
      pixel: 'google_analytics',
      id: gaTrackingId
    });
    return Promise.resolve(false);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.type = 'text/plain';
    script.async = true;
    script.setAttribute('data-cookieconsent', 'marketing');
    script.onerror = function () {
      reject('failed to load gtag script');
    };
    document.head.appendChild(script);
    resolve(true);

    consoleLogger.debug('⚙️ Google Analytics added to DOM');
  });
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const trackPage = (page_title: string) => {
  const trackingId = environmentService.getGaTrackingId();

  if (!trackingId) {
    consoleLogger.debug('⚠️ GA_TRACKING_ID is not set. TrackPage was triggered', {
      page_title
    });
  }
  if (!isGtagLoaded()) {
    return;
  }

  window.gtag('config', trackingId, {
    page_title: page_title
  });
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (!environmentService.getGaTrackingId()) {
    consoleLogger.info('⚠️ GA_TRACKING_ID is not set. TrackEvent was triggered', {
      action,
      category,
      label,
      value
    });
  }
  if (!isGtagLoaded()) {
    return;
  }

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value
  });
};

// Legacy/Deprecated: prefer using regular event tracking
export const trackConversionEvent = (nonPrefixedSendTo: string, transactionId?: string) => {
  const trackingId = environmentService.getGaTrackingId();

  if (!trackingId) {
    consoleLogger.debug('⚠️ GA_TRACKING_ID is not set. trackConversionEvent was triggered', {
      nonPrefixedSendTo,
      transactionId
    });
  }
  if (!isGtagLoaded()) {
    return;
  }

  consoleLogger.info('Sending conversion event to GA..');
  window.gtag('event', 'conversion', {
    send_to: `${trackingId}/${nonPrefixedSendTo}`,
    transaction_id: transactionId
  });
};
